import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import axios from "axios";
import { API } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import {
  Plus,
  Calendar as CalendarIcon,
  Users,
  Clock,
  MapPin,
  Trash2,
  Loader2
} from "lucide-react";

const MeetingsPage = () => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [meetingType, setMeetingType] = useState("");
  const [meetingDate, setMeetingDate] = useState(null);
  const [meetingTime, setMeetingTime] = useState("09:00");
  const [duration, setDuration] = useState("60");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState([]);

  const usersMap = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])),
    [users]
  );

  const getInitials = (name) => {
    const parts = (name || "").split(" ");
    return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  };

  useEffect(() => {
    fetchMeetings();
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/meetings`);
      setMeetings(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des réunions");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!meetingDate || !meetingType || !title) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/meetings`, {
        title,
        meeting_type: meetingType,
        date: format(meetingDate, "yyyy-MM-dd"),
        time: meetingTime,
        duration_minutes: parseInt(duration),
        location,
        description,
        participants: selectedParticipants
      });
      toast.success("Réunion créée avec succès !");
      setIsDialogOpen(false);
      resetForm();
      fetchMeetings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (meetingId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette réunion ?")) return;
    
    try {
      await axios.delete(`${API}/meetings/${meetingId}`);
      toast.success("Réunion supprimée");
      fetchMeetings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setTitle("");
    setMeetingType("");
    setMeetingDate(null);
    setMeetingTime("09:00");
    setDuration("60");
    setLocation("");
    setDescription("");
    setSelectedParticipants([]);
  };

  const getMeetingTypeLabel = (type) => {
    const labels = {
      suivi: "Suivi",
      entretien: "Entretien",
      formation: "Formation",
      entretien_annuel: "Entretien annuel",
      entretien_trimestriel: "Entretien trimestriel",
      autre: "Autre"
    };
    return labels[type] || type;
  };

  const getMeetingTypeBadge = (type) => {
    const config = {
      suivi: { className: "bg-blue-100 text-blue-800 border-blue-200" },
      entretien: { className: "bg-purple-100 text-purple-800 border-purple-200" },
      formation: { className: "bg-green-100 text-green-800 border-green-200" },
      entretien_annuel: { className: "bg-violet-100 text-violet-900 border-violet-200" },
      entretien_trimestriel: { className: "bg-indigo-100 text-indigo-800 border-indigo-200" },
      autre: { className: "bg-gray-100 text-gray-800 border-gray-200" }
    };
    const { className } = config[type] || config.autre;
    return <Badge className={`${className} border`}>{getMeetingTypeLabel(type)}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      scheduled: { label: "Planifiée", className: "bg-[#E8F5E9] text-[#1A4D2E] border-[#1A4D2E]/20" },
      completed: { label: "Terminée", className: "bg-gray-100 text-gray-600 border-gray-200" },
      cancelled: { label: "Annulée", className: "bg-red-100 text-red-800 border-red-200" }
    };
    const { label, className } = config[status] || config.scheduled;
    return <Badge className={`${className} border`}>{label}</Badge>;
  };

  const canDeleteMeeting = (meeting) => {
    return meeting.organizer_id === user?.id || user?.role === "hr";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meetings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
            Réunions
          </h1>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1 text-sm md:text-base">
            Planifiez et gérez vos réunions de suivi
          </p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal={true}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] rounded-full"
              data-testid="new-meeting-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle réunion
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg z-50 overflow-hidden">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display'] text-xl">
                Planifier une réunion
              </DialogTitle>
              <DialogDescription>
                Créez une nouvelle réunion et invitez des participants.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto overflow-x-hidden pr-1">
              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  placeholder="Ex: Réunion de suivi hebdomadaire"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="meeting-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="meetingType">Type de réunion *</Label>
                <Select value={meetingType} onValueChange={setMeetingType}>
                  <SelectTrigger data-testid="meeting-type-select">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suivi">Suivi</SelectItem>
                    <SelectItem value="entretien">Entretien</SelectItem>
                    <SelectItem value="formation">Formation</SelectItem>
                    <SelectItem value="entretien_annuel">Entretien annuel</SelectItem>
                    <SelectItem value="entretien_trimestriel">Entretien trimestriel</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 min-w-0">
                  <Label>Date *</Label>
                  <Popover modal={true}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="meeting-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                        {meetingDate ? format(meetingDate, "dd/MM/yyyy") : "Sélectionner"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[100]" align="start">
                      <Calendar
                        mode="single"
                        selected={meetingDate}
                        onSelect={setMeetingDate}
                        locale={fr}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2 min-w-0">
                  <Label htmlFor="time">Heure *</Label>
                  <Input
                    id="time"
                    type="time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    className="w-full min-w-0"
                    data-testid="meeting-time"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 min-w-0">
                  <Label htmlFor="duration">Durée</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger data-testid="meeting-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="60">1 heure</SelectItem>
                      <SelectItem value="90">1h30</SelectItem>
                      <SelectItem value="120">2 heures</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 min-w-0">
                  <Label htmlFor="location">Lieu</Label>
                  <Input
                    id="location"
                    placeholder="Salle A, Teams, etc."
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    data-testid="meeting-location"
                  />
                </div>
              </div>

              {(user?.role === "hr" || user?.role === "manager") && users.length > 0 && (
                <div className="space-y-2">
                  <Label>Participants</Label>
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-gray-50 max-h-32 overflow-y-auto">
                    {users.filter(u => u.id !== user?.id).map((u) => (
                      <Badge
                        key={u.id}
                        variant={selectedParticipants.includes(u.id) ? "default" : "outline"}
                        className={`cursor-pointer transition-all ${
                          selectedParticipants.includes(u.id)
                            ? "bg-[#1A4D2E] hover:bg-[#143D24]"
                            : "hover:bg-[#E8F5E9]"
                        }`}
                        onClick={() => {
                          if (selectedParticipants.includes(u.id)) {
                            setSelectedParticipants(selectedParticipants.filter(id => id !== u.id));
                          } else {
                            setSelectedParticipants([...selectedParticipants, u.id]);
                          }
                        }}
                      >
                        {u.first_name} {u.last_name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Description (optionnel)</Label>
                <Textarea
                  id="description"
                  placeholder="Ordre du jour, objectifs..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="meeting-description"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-[#1A4D2E] hover:bg-[#143D24]"
                  disabled={isSubmitting}
                  data-testid="submit-meeting"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Créer"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Meetings Grid */}
      {meetings.length === 0 ? (
        <Card className="card-neumorphic">
          <CardContent className="py-12 text-center text-[#9CA3AF]">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucune réunion planifiée</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {meetings.map((meeting, index) => (
            <Card 
              key={meeting.id} 
              className={`card-neumorphic hover:-translate-y-1 transition-all duration-300 animate-fade-in stagger-${(index % 5) + 1}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  {getMeetingTypeBadge(meeting.meeting_type)}
                  {getStatusBadge(meeting.status)}
                </div>
                
                <h3 className="font-semibold text-lg text-[#111827] dark:text-[#D6E4F5] mb-2">{meeting.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  {(() => {
                    const u = usersMap[meeting.organizer_id];
                    return (
                      <Avatar className="w-6 h-6 shrink-0">
                        <AvatarImage src={u?.avatar_url} />
                        <AvatarFallback className="text-[10px] bg-[#1A4D2E]/10 text-[#1A4D2E]">
                          {getInitials(meeting.organizer_name)}
                        </AvatarFallback>
                      </Avatar>
                    );
                  })()}
                  <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">
                    Organisé par <ClickableUser userId={meeting.organizer_id}>{meeting.organizer_name}</ClickableUser>
                  </p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-[#4B5563]">
                    <CalendarIcon className="w-4 h-4 text-[#1A4D2E]" />
                    <span>{meeting.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#4B5563]">
                    <Clock className="w-4 h-4 text-[#1A4D2E]" />
                    <span>{meeting.time} ({meeting.duration_minutes} min)</span>
                  </div>
                  {meeting.location && (
                    <div className="flex items-center gap-2 text-[#4B5563]">
                      <MapPin className="w-4 h-4 text-[#1A4D2E]" />
                      <span>{meeting.location}</span>
                    </div>
                  )}
                </div>

                {meeting.description && (
                  <p className="mt-4 text-sm text-[#9CA3AF] line-clamp-2">
                    {meeting.description}
                  </p>
                )}

                {canDeleteMeeting(meeting) && meeting.status === "scheduled" && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(meeting.id)}
                      data-testid={`delete-meeting-${meeting.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MeetingsPage;
